import {
  getTranslatedViewHeaderDescription,
  getViewHeaderDescriptionFromContext,
  useApiClient,
  useEntityMetadataIncludeExternal,
  useHistoryContext,
  useModuleFederationContext,
  useRouteParams,
  useSitemapItems,
  useTranslation,
  useUrlContext,
  useViewBaseMetadata,
} from '@msdk/common'
import {
  CountBehaviour,
  EntityMetadata,
  EntityModel,
  OpenMode,
  readBaseMetadataByNameAndModule,
  readEntityModelIncludeExternal,
  ViewModel,
} from '@msdk/definitions'
import {
  $countCollection,
  CountCollectionEntry,
  ImportedComponentErrorBoundary,
  PageView,
  resolveItem,
  useSitemapViews,
  useViewContext,
  ViewContextProvider,
  ViewHeader,
} from '@msdk/forms'
import { PageInformation } from '@msdk/functional-components'
import { styled, Tabs, TabsContainer } from '@msdk/ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useRecoilState } from 'recoil'

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
`

const updateTabContext = (
  context: ReturnType<typeof useUrlContext>,
  history: ReturnType<typeof useHistoryContext>,
  moduleName: string | undefined,
  tabId: string,
  clearPreviousFilters = false,
) => {
  const current = { ...context.context }
  context.updateTabContext(current, moduleName, tabId)

  if (clearPreviousFilters && (current['f'] || current['cf'])) {
    delete current['cf']
    delete current['f']
  }

  const serialized = context.serialize(current)
  const url = new URL(window.location.href)
  url.searchParams.set('s', serialized)
  window.history.replaceState({}, '', url.toString())
  history.updateCurrentSearch(url.searchParams.toString())
  context.update(`?s=${serialized}`)
}

const buildViewHeader = (
  entityNameForViewHeader: string,
  moduleNameForViewHeader: string,
  isInitializedForViewHeader: boolean,
  model: EntityModel,
  viewHeaderDescriptionForViewHeader?: string,
) => {
  const viewModelForViewHeader = model.getView(moduleNameForViewHeader) as ViewModel
  const openMode = viewModelForViewHeader.openMode === OpenMode.Modal ? 'Modal' : 'Page'

  return (
    <ViewHeader
      entityName={entityNameForViewHeader}
      viewName={moduleNameForViewHeader}
      mode={openMode}
      description={viewHeaderDescriptionForViewHeader}
      isInitialized={isInitializedForViewHeader}
    />
  )
}

function ViewContent() {
  const { entityName, moduleName, itemId } = useRouteParams()
  const views = moduleName?.split(',') as string[]
  const t = useTranslation()
  const api = useApiClient()
  const sitemapItems = useSitemapItems()

  const [countCollection, setCountCollection] = useRecoilState($countCollection)

  const context = useUrlContext()
  const contextRef = useRef(context)
  contextRef.current = context
  const location = useLocation()
  const pairs = useSitemapViews(entityName as string, moduleName as string)

  const findTabIndextFromTabId = (contextTabId: string) => {
    if (!Number.isInteger(Number(contextTabId))) {
      const tabIndex = pairs.findIndex((pair) => pair.viewName === contextTabId)
      return tabIndex > 0 ? tabIndex : 0
    }

    return Number(contextTabId)
  }

  const { contextModuleName, contextTabId } = context.getTabContext(moduleName)
  const parsedTabIndex = findTabIndextFromTabId(contextTabId)
  const startingTab =
    contextModuleName === moduleName && parsedTabIndex >= 0 && parsedTabIndex < pairs.length ? parsedTabIndex : 0

  const history = useHistoryContext()
  const historyRef = useRef(history)
  historyRef.current = history

  const [currentTab, setCurrentTab] = useState<number>(0)
  const currentPairFromTab = pairs[currentTab] ?? pairs[0]
  const currentEntityNameFromTab = currentPairFromTab?.entityName ?? entityName
  const currentViewName = currentPairFromTab?.viewName ?? views[0]
  const model = useEntityMetadataIncludeExternal(currentEntityNameFromTab, currentViewName) as EntityModel

  const viewHeaderDescriptionObj = getViewHeaderDescriptionFromContext(context.context)

  let viewHeaderDescription = getTranslatedViewHeaderDescription(viewHeaderDescriptionObj, t)

  if (itemId) {
    // If there is an itemId in the context we can use that to resolve
    // the sitemap item and recover the view header description this is better than
    // relying on the context as it will work even with a base url without any context
    const item = sitemapItems.find((i) => i.id === itemId)
    if (item?.viewHeaderDescription) {
      viewHeaderDescription = getTranslatedViewHeaderDescription(item.viewHeaderDescription, t)
    }
  }

  const viewModel = model.getView(currentViewName as string)

  const baseMetadata = useViewBaseMetadata(entityName as string, views[0]) as EntityMetadata
  const modules = useModuleFederationContext()
  const msModule = modules.loadedModules[baseMetadata.msProvider]
  const ImportedComponent = resolveItem(msModule, viewModel?.componentDefinition?.fullName)

  function findViewNameForTabIndex(newValue: number) {
    return pairs[newValue]?.viewName ?? ''
  }

  const getViewLabel = (currentEntityName: string, viewName: string) => {
    const entityModel = readEntityModelIncludeExternal(currentEntityName, baseMetadata.msProvider) as EntityModel
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const viewModel = entityModel.getView(viewName) as ViewModel
    const count = countCollection.getItem(currentEntityName, viewName)
    if (viewModel.countBehaviour === CountBehaviour.Enabled) {
      return `${t(viewModel.idml, viewModel.description)}${count !== undefined ? `: ${count}` : ''}`
    }
    return `${t(viewModel.idml, viewModel.description)}`
  }

  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const viewContext = useViewContext()

  const onTabChange = (newValue: number) => {
    setIsInitialized(false)
    setCurrentTab(newValue)
    const tabViewName = findViewNameForTabIndex(newValue)
    updateTabContext(context, history, moduleName, tabViewName, true)
  }

  useEffect(() => {
    if (history.processedLocationKey !== location.key) return

    if (moduleName !== contextModuleName) {
      const tabViewName = findViewNameForTabIndex(0)
      updateTabContext(contextRef.current, historyRef.current, moduleName, tabViewName)
    }
  }, [moduleName, contextModuleName, history.processedLocationKey, location.key])

  useEffect(() => {
    if (viewContext.context.isInitialized) {
      setIsInitialized(true)
    }
  }, [viewContext])

  const counters = pairs.map((p) => countCollection.getItem(p.entityName, p.viewName))
  const [currentCounters, setCurrentCounters] = useState(counters)

  const [shouldUpdateCounters, setShouldUpdateCounters] = useState(false)
  useEffect(() => {
    setCurrentCounters(counters)
    if (counters[currentTab] != currentCounters[currentTab]) {
      setShouldUpdateCounters(true)
    }
  }, [currentTab, ...counters])

  useEffect(() => {
    setShouldUpdateCounters(false)
    if (!shouldUpdateCounters) return
    const fetch = async () => {
      const entries: CountCollectionEntry[] = []
      for (let i = 0; i < pairs.length; i++) {
        const currentPair = pairs[i]
        const entityModel = readEntityModelIncludeExternal(
          currentPair.entityName,
          baseMetadata.msProvider,
        ) as EntityModel
        const pairViewModel = entityModel.getView(currentPair.viewName) as ViewModel
        if (pairViewModel.countBehaviour === CountBehaviour.Enabled) {
          const metadata = readBaseMetadataByNameAndModule(
            currentPair.entityName,
            currentPair.viewName,
          ) as EntityMetadata
          const countRes = await api.entity.getViewCount(
            metadata.msProvider,
            currentPair.entityName,
            currentPair.viewName,
          )
          entries.push({
            entityName: currentPair.entityName,
            viewName: currentPair.viewName,
            count: countRes.count,
          })
        }
      }
      setCountCollection((curr) => {
        const newCollection = curr.clone()
        entries.forEach((entry) => {
          newCollection.upsertItem(entry.entityName, entry.viewName, entry.count)
        })
        return newCollection
      })
    }
    fetch().catch(console.error)
  }, [shouldUpdateCounters])

  const renderTabs = useCallback(() => {
    return (
      <Tabs
        startingTab={startingTab}
        onChange={onTabChange}
        childs={pairs.map((p) => {
          return {
            label: getViewLabel(p.entityName, p.viewName),
            content: <PageView entityName={p.entityName} moduleName={p.viewName} />,
          }
        })}
      />
    )
  }, [countCollection, pairs, getViewLabel, onTabChange])

  return (
    <>
      <PageInformation pageTitle={t(model.idml, model.description)} />
      {pairs.length === 1 && ImportedComponent && (
        <ImportedComponentErrorBoundary>
          <ImportedComponent />
        </ImportedComponentErrorBoundary>
      )}
      {pairs.length === 1 && !ImportedComponent && (
        <>
          {buildViewHeader(entityName as string, moduleName as string, isInitialized, model, viewHeaderDescription)}
          <PageView entityName={entityName as string} moduleName={moduleName as string} />
        </>
      )}
      {pairs.length > 1 && (
        <>
          {buildViewHeader(
            pairs[currentTab].entityName as string,
            pairs[currentTab].viewName as string,
            isInitialized,
            model,
            viewHeaderDescription,
          )}
          <TabsContainer>{renderTabs()}</TabsContainer>
        </>
      )}
    </>
  )
}

export default function View() {
  const { pathname, search } = useLocation()

  return (
    <Wrapper key={pathname + search}>
      <ViewContextProvider>
        <ViewContent />
      </ViewContextProvider>
    </Wrapper>
  )
}
